/**
 * a8e (Articulate) Command
 * Install, configure, and manage the a8e coding CLI as a local sub-agent executor
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync } from 'fs';
import { promisify } from 'util';
import { exec as execCallback } from 'child_process';
import { isAuthenticated } from '../utils/config.js';
import { getOrCreateA8eApiKey } from '../api/apikey.js';
import { ArticulateExecutor, A8E_PATHS } from '../worker/executors/articulate.js';
import * as output from '../utils/output.js';

const exec = promisify(execCallback);

export const a8eCommand = new Command('a8e')
    .description('Manage the a8e coding CLI (local sub-agent executor for coding tasks)');

// ── paean a8e install ───────────────────────────────────────────────

a8eCommand
    .command('install')
    .description('Install a8e coding CLI and configure it with your Paean AI account')
    .option('--skip-config', 'Only install the binary, skip provider configuration')
    .option('--model <model>', 'Default model to use', A8E_PATHS.defaultModel)
    .action(async (options) => {
        console.log('');
        console.log(chalk.bold.cyan('  ╔════════════════════════════════════════════╗'));
        console.log(chalk.bold.cyan('  ║') + chalk.bold.white('        a8e Coding CLI Setup') + chalk.bold.cyan('              ║'));
        console.log(chalk.bold.cyan('  ╚════════════════════════════════════════════╝'));
        console.log('');

        const executor = new ArticulateExecutor();

        // Step 1: Check if a8e is already installed
        const available = await executor.isAvailable();

        if (available) {
            const status = await executor.checkAvailability();
            output.success(`a8e is already installed${status.version ? ` (v${status.version})` : ''}`);
            if (status.binaryPath) {
                output.dim(`  Binary: ${status.binaryPath}`);
            }
        } else {
            // Install a8e binary
            output.info('Installing a8e coding CLI...');
            console.log('');

            try {
                const { stdout: installOutput } = await exec(
                    'curl -fsSL https://a8e.ai/install.sh | bash',
                    { timeout: 120000 },
                );
                if (installOutput) console.log(chalk.dim(installOutput));

                // Verify installation
                const nowAvailable = await executor.isAvailable();
                if (!nowAvailable) {
                    output.error('Installation completed but a8e binary not found in PATH.');
                    output.dim('You may need to restart your shell or add the install directory to PATH.');
                    process.exit(1);
                }

                output.success('a8e installed successfully');
            } catch (error) {
                output.error(`Failed to install a8e: ${(error as Error).message}`);
                output.dim('Try installing manually: curl -fsSL https://a8e.ai/install.sh | bash');
                process.exit(1);
            }
        }

        if (options.skipConfig) {
            console.log('');
            output.dim('Skipped provider configuration. Run `paean a8e configure` to set up later.');
            return;
        }

        // Step 2: Provision API key and configure
        console.log('');
        output.info('Configuring a8e with Paean AI provider...');

        if (!isAuthenticated()) {
            output.warning('Not logged in to Paean. Run `paean login` first, then `paean a8e configure`.');
            return;
        }

        const spin = output.spinner('Provisioning API key...').start();

        try {
            const keyResult = await getOrCreateA8eApiKey();

            if (!keyResult.success || !keyResult.key) {
                spin.stop();
                output.error(`Failed to provision API key: ${keyResult.error}`);
                output.dim('You can configure manually: a8e configure');
                return;
            }

            spin.text = 'Writing a8e configuration...';

            executor.configureForPaean(keyResult.key, options.model);

            spin.stop();
            output.success('a8e configured with Paean AI provider');
            console.log('');
            output.tableRow('Provider', A8E_PATHS.defaultProvider, 18);
            output.tableRow('Model', options.model || A8E_PATHS.defaultModel, 18);
            output.tableRow('Config', A8E_PATHS.configFile, 18);
            console.log('');
            output.dim('a8e is ready. Paean CLI will automatically use it for coding tasks.');
            output.dim('Test it with: a8e run --text "hello"');
        } catch (error) {
            spin.stop();
            output.error(`Configuration failed: ${(error as Error).message}`);
        }

        console.log('');
    });

// ── paean a8e status ────────────────────────────────────────────────

a8eCommand
    .command('status')
    .description('Check a8e installation and configuration status')
    .action(async () => {
        console.log('');
        console.log(chalk.bold.blue('  a8e Status\n'));

        const executor = new ArticulateExecutor();
        const available = await executor.isAvailable();

        if (!available) {
            output.tableRow('Installed', chalk.red('No'), 18);
            output.dim('  Install with: paean a8e install');
            console.log('');
            return;
        }

        const status = await executor.checkAvailability();
        output.tableRow('Installed', chalk.green('Yes'), 18);
        if (status.version) {
            output.tableRow('Version', status.version, 18);
        }
        if (status.binaryPath) {
            output.tableRow('Binary', status.binaryPath, 18);
        }

        // Read a8e config
        const config = executor.readA8eConfig();
        output.tableRow('Provider', config.provider || chalk.dim('not set'), 18);
        output.tableRow('Model', config.model || chalk.dim('not set'), 18);
        output.tableRow('API Key', config.hasApiKey ? chalk.green('configured') : chalk.yellow('not set'), 18);
        output.tableRow('Config File', existsSync(A8E_PATHS.configFile) ? A8E_PATHS.configFile : chalk.dim('not found'), 18);

        if (!config.provider || !config.hasApiKey) {
            console.log('');
            output.warning('a8e is not fully configured. Run `paean a8e configure` to set up.');
        }

        console.log('');
    });

// ── paean a8e configure ─────────────────────────────────────────────

a8eCommand
    .command('configure')
    .description('Configure a8e with Paean AI provider and API key')
    .option('--model <model>', 'Model to use', A8E_PATHS.defaultModel)
    .option('--api-key <key>', 'Use a specific API key instead of auto-provisioning')
    .action(async (options) => {
        console.log('');
        output.header('Configure a8e');

        const executor = new ArticulateExecutor();
        const available = await executor.isAvailable();

        if (!available) {
            output.error('a8e is not installed. Run `paean a8e install` first.');
            console.log('');
            return;
        }

        let apiKey = options.apiKey as string | undefined;

        if (!apiKey) {
            if (!isAuthenticated()) {
                output.error('Not logged in. Run `paean login` first, or provide --api-key manually.');
                console.log('');
                return;
            }

            const spin = output.spinner('Provisioning API key...').start();
            const keyResult = await getOrCreateA8eApiKey();
            spin.stop();

            if (!keyResult.success || !keyResult.key) {
                output.error(`Failed to provision API key: ${keyResult.error}`);
                console.log('');
                return;
            }
            apiKey = keyResult.key;
        }

        executor.configureForPaean(apiKey, options.model);

        output.success('a8e configuration updated');
        console.log('');
        output.tableRow('Provider', A8E_PATHS.defaultProvider, 18);
        output.tableRow('Model', options.model || A8E_PATHS.defaultModel, 18);
        console.log('');
    });

// ── paean a8e uninstall ─────────────────────────────────────────────

a8eCommand
    .command('uninstall')
    .description('Remove a8e configuration (does not remove the binary)')
    .option('--force', 'Skip confirmation')
    .action(async (options) => {
        if (!options.force) {
            const readline = await import('readline');
            const rl = readline.createInterface({
                input: process.stdin,
                output: process.stdout,
            });
            const answer = await new Promise<string>((resolve) => {
                rl.question('Remove a8e Paean configuration? (y/n): ', resolve);
            });
            rl.close();

            if (answer.toLowerCase() !== 'y' && answer.toLowerCase() !== 'yes') {
                output.dim('Cancelled.');
                return;
            }
        }

        const fs = await import('fs');

        let removed = false;
        if (existsSync(A8E_PATHS.configFile)) {
            // Only remove Paean-specific keys, not the whole config
            try {
                let content = fs.readFileSync(A8E_PATHS.configFile, 'utf-8');
                content = content.replace(/^A8E_PROVIDER:.*\n?/m, '');
                content = content.replace(/^A8E_MODEL:.*\n?/m, '');
                fs.writeFileSync(A8E_PATHS.configFile, content, 'utf-8');
                removed = true;
            } catch {
                output.warning('Could not update config file');
            }
        }

        if (existsSync(A8E_PATHS.secretsFile)) {
            try {
                let content = fs.readFileSync(A8E_PATHS.secretsFile, 'utf-8');
                content = content.replace(/^PAEAN_AI_API_KEY:.*\n?/m, '');
                fs.writeFileSync(A8E_PATHS.secretsFile, content, 'utf-8');
                removed = true;
            } catch {
                output.warning('Could not update secrets file');
            }
        }

        if (removed) {
            output.success('Paean AI configuration removed from a8e');
        } else {
            output.dim('No Paean configuration found in a8e');
        }

        output.dim('The a8e binary was not removed. Uninstall it manually if desired.');
        console.log('');
    });
